import {computed, ref, shallowRef} from 'vue';
import {defineStore} from 'pinia';

import {REGIONS, RO_NAME} from '@/core/constants/regions';
import {USER_LOCATION} from '@/core/constants/cookies';
import suggestion, {SERVICE_GEOLOCATE, SUGGESTION_TYPES} from '@/core/utils/suggestionsApiClinet';
import {
    STORAGE_GEO_KEY,
    geolocation,
    checkGeolocationPermissions,
    STATUS_DENIED,
} from '@/core/plugins/geolocation';
import {getAppContext} from '@/core/plugins/appContext';

export const useRegionsStore = defineStore('regions', () => {
    const {$api, $cookies} = getAppContext();

    const regionsList = ref([]);
    const userRegion = ref(null);
    const isRegionModalActive = shallowRef(false);

    const activeRegion = computed(() => {
        return userRegion.value
            ? userRegion.value
            : REGIONS.find((region) => region.name.toLowerCase() === RO_NAME.toLowerCase());
    });

    function setRegionsList(regions) {
        regionsList.value = regions;
    }

    function setUserRegion(region) {
        userRegion.value = typeof region === 'string' ? JSON.parse(region) : region;
    }

    function toggleRegionModalActivity(activity = false) {
        isRegionModalActive.value = activity;
    }

    async function getRegionsList() {
        if (regionsList.value.length) {
            return;
        }

        try {
            const {data} = await $api.referenceData.regions();
            setRegionsList(data.data);

            return data.data;
        } catch (error) {
            console.error(error);
        }
    }

    function checkUserLocationCookie() {
        const regionCookie = $cookies.get(USER_LOCATION) ?? null;

        if (regionCookie) {
            setUserRegion(regionCookie);
        }

        return Promise.resolve(regionCookie);
    }

    async function detectUserCoords() {
        const storageCoords = window?.localStorage?.getItem(STORAGE_GEO_KEY) ?? null;

        if (storageCoords) {
            return JSON.parse(storageCoords);
        }

        const permission = await checkGeolocationPermissions(toggleRegionModalActivity);

        if (permission === STATUS_DENIED) {
            toggleRegionModalActivity(true);

            return null;
        }

        try {
            const coords = await geolocation();

            return coords;
        } catch (error) {
            console.warn(error);
        }
    }

    async function detectUserLocationByCoords() {
        if (userRegion.value) {
            return;
        }

        const coords = await detectUserCoords();

        if (!coords) {
            toggleRegionModalActivity(true);

            return null;
        }

        const {data} = await suggestion[SUGGESTION_TYPES.SUGGESTIONS]({
            service: SERVICE_GEOLOCATE,
            lat: coords.latitude,
            lon: coords.longitude,
        });

        if (!data) {
            toggleRegionModalActivity(true);

            return null;
        }

        const region = REGIONS.find((region) => {
            return region.shortName.toLocaleLowerCase() === data.region.toLocaleLowerCase();
        }) ?? null;

        setUserRegion(region);

        if (!region) {
            toggleRegionModalActivity(true);
        }

        return region;
    }

    return {
        regionsList,
        userRegion,
        isRegionModalActive,
        activeRegion,
        setRegionsList,
        getRegionsList,
        setUserRegion,
        toggleRegionModalActivity,
        checkUserLocationCookie,
        detectUserCoords,
        detectUserLocationByCoords,
    };
});
